<template>
  <div class="c-popover" :class="classy" :style="style">
    <header v-if="$slots.header" class="header">
      <slot name="header" />
    </header>
    <div class="content">
      <slot />
    </div>
  </div>
</template>

<script>
import getParents from '../../helpers/getParents'

const defaultBoundingClientRect = { left: 0, top: 0, right: 0, bottom: 0, x: 0, y: 0, width: 0, height: 0 }

export default {
  props: {
    target: {
      type: [String, HTMLElement, Object],
      required: true
    },
    arrow: {
      type: Boolean,
      default: true
    },
    position: {
      validator (value) {
        const positions = ['top', 'bottom']
        const directions = ['right', 'center', 'left']
        const valid = Array.isArray(value) && positions.includes(value[0]) && directions.includes(value[1])
        return valid
      }
    }
  },
  data () {
    return {
      style: {}
    }
  },
  watch: {
    target () {
      this.calcStyle()
    }
  },
  computed: {
    classy () {
      const [ position, direction ] = this.position
      const classy = {
        [ this.arrow
          ? `-${position} -${direction}`
          : '-no-arrow' ]: true
      }
      return classy
    },
    element () {
      if (!this.target) return null
      if (typeof this.target === 'string') {
        return document.querySelector(this.target)
      }
      if (this.target instanceof HTMLElement) {
        return this.target
      }
      return this.target && this.target.$el ? this.target.$el : null
    }
  },
  methods: {
    calcStyle () {
      const popoverPosition = this.$el ? this.$el.getBoundingClientRect() : defaultBoundingClientRect
      const targetPosition = this.element ? this.element.getBoundingClientRect() : defaultBoundingClientRect

      let posX = targetPosition.left + (targetPosition.width / 2)
      let posY

      if (this.position[0] === 'top')
        posY = targetPosition.top + targetPosition.height + 8

      if (this.position[0] === 'bottom')
        posY = targetPosition.top - popoverPosition.height - 8

      if (this.position[1] === 'right')
        posX = posX - popoverPosition.width + 20

      if (this.position[1] === 'left')
        posX = posX - 20

      if (this.position[1] === 'center')
        posX = posX - popoverPosition.width / 2

      const style = {
        top: posY + 'px',
        left: posX + 'px'
      }
      this.style = style
    },
    blurEmitter (event) {
      const targets = event.target ? [event.target, ...getParents(event.target)] : []
      const isTarget = targets.includes(this.element)
      const isOutside = !targets.includes(this.$el)

      if (!isTarget && isOutside) this.$emit('blur')
    }
  },
  mounted () {
    this.calcStyle()
    window.addEventListener('click', this.blurEmitter)
    window.addEventListener('resize', this.calcStyle)
    window.addEventListener('scroll', this.calcStyle)
  },
  beforeDestroy () {
    window.removeEventListener('click', this.blurEmitter)
    window.removeEventListener('resize', this.calcStyle)
    window.removeEventListener('scroll', this.calcStyle)
  }
}
</script>

<style lang="scss">
@import '~@/style/reference';

$c-popover-arrow-size: 4px                      !default;
$c-popover-background: #fff                   !default;
$c-popover-border: 1px solid $border-color      !default;
$c-popover-header-background: $background-color !default;

@mixin box-shadow-as-border($border, $position: top) {
  $size: calc(nth($border, 1) / 2);
  $color: nth($border, 3);

  @if ($position == top) {
    box-shadow: -#{$size + 1} -#{$size + 1} 0 #{$size - 1} $color;
  }
}

%c-popover-arrow-top {
  &::before {
    @extend %c-popover-arrow;
    top: 0;
  }
}
%c-popover-arrow-bottom {
  &::before {
    @extend %c-popover-arrow;
    bottom: -4px;
  }
}

.c-popover {
  position: fixed;
  background-color: $c-popover-background;
  border: $c-popover-border;
  border-radius: 3px;
  box-shadow:
    0 0 1px rgba(#8e92a7, 0.1),
    0 0 6px rgba(#8e92a7, 0.2);
    z-index: 999;

  & > .header,
  & > .content { box-sizing: border-box; }

  & > .header { @extend %c-popover-header; }

  &.-top {
    @extend %c-popover-arrow-top;
  }

  &.-bottom {
    @extend %c-popover-arrow-bottom;
  }

  &.-center {
    &::before {
      left: 50%;
      transform: translate(-50%, -$c-popover-arrow-size) rotate(45deg);
    }
  }

  &.-right {
    &::before, &::after {
      right: 12px;
      transform: translate(-50%, -$c-popover-arrow-size) rotate(45deg);
    }
  }

  &.-left {
    &::before, &::after {
      left: 12px;
      transform: translate(50%, -$c-popover-arrow-size) rotate(45deg);
    }
  }
}

%c-popover-arrow {
  position: absolute;
  width: 0;
  height: 0;
  z-index: 1;
  content: '';
  border-width: $c-popover-arrow-size $c-popover-arrow-size $c-popover-arrow-size $c-popover-arrow-size;
  border-style: solid solid solid solid;
  background-color: $c-popover-background;
  @include box-shadow-as-border($c-popover-border, top);
  transform: rotate(45deg);
  border-color: $c-popover-background;
}

%c-popover-header {
  background-color: $c-popover-header-background;
  border-bottom: 1px solid #e9eaee;
}
</style>



// WEBPACK FOOTER //
// src/components/CComponents/CPopover.vue